/**
 * CMS service — fetches page content from Wagtail.
 *
 * Uses a stale-while-revalidate strategy with localStorage:
 *   1. Return cached content immediately (no flash of hardcoded defaults)
 *   2. Fetch fresh data from API in background
 *   3. Update localStorage + notify caller via onUpdate callback
 *
 * This means after the first visit, pages always show real CMS content
 * instantly, with silent background updates when content changes.
 */

import axios from 'axios';
import store from '@/store';

const BASE = () => store.getters.baseUrl.replace(/\/$/, '');
const CACHE_PREFIX = 'cms_cache_';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours — localStorage entries expire after this

function apiGet(path) {
  return axios.get(`${BASE()}/${path}`).then(r => r.data);
}

function previewToken() {
  const p = new URLSearchParams(window.location.search);
  return p.get('preview') === '1' ? p.get('token') : null;
}

/** Read from localStorage, return null if missing or expired */
function readCache(key) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(CACHE_PREFIX + key); return null; }
    return data;
  } catch { return null; }
}

/** Write to localStorage */
function writeCache(key, data) {
  try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/**
 * Stale-while-revalidate fetch.
 * Returns a promise that resolves with the best available data immediately,
 * then calls onUpdate(freshData) once the API responds with newer content.
 *
 * @param {string}   slug     - cache key / API slug
 * @param {object}   defaults - hardcoded fallback (used only on very first visit)
 * @param {Function} onUpdate - called with fresh data after background fetch completes
 */
async function fetchPage(slug, defaults, onUpdate) {
  const token = previewToken();

  // Preview mode: skip cache entirely, always fetch fresh
  if (token) {
    try {
      const data = await apiGet(`cms-api/preview/?token=${encodeURIComponent(token)}`);
      return { ...defaults, ...data };
    } catch { return defaults; }
  }

  // 1. Return cached content immediately (if available), otherwise defaults
  const cached = readCache(slug);
  const immediate = cached ? { ...defaults, ...cached } : defaults;

  // 2. Fetch fresh in background
  apiGet(`cms-api/page/${slug}/`)
    .then(fresh => {
      const merged = { ...defaults, ...fresh };
      writeCache(slug, fresh);
      // Only call onUpdate if content actually changed
      if (JSON.stringify(fresh) !== JSON.stringify(cached) && onUpdate) {
        onUpdate(merged);
      }
    })
    .catch(() => {});

  return immediate;
}

/**
 * Each exported function accepts (defaults, onUpdate).
 * Pass an onUpdate callback to receive background refresh updates.
 */
export const cmsHome     = (defaults, onUpdate) => fetchPage('home',     defaults, onUpdate);
export const cmsAbout    = (defaults, onUpdate) => fetchPage('about',    defaults, onUpdate);
export const cmsContact  = (defaults, onUpdate) => fetchPage('contact',  defaults, onUpdate);
export const cmsCoverage = (defaults, onUpdate) => fetchPage('coverage', defaults, onUpdate);
export const cmsFaq      = (defaults, onUpdate) => fetchPage('faq',      defaults, onUpdate);

/** Footer — same stale-while-revalidate pattern */
export async function cmsFooter(defaults, onUpdate) {
  const cached = readCache('footer');
  const immediate = cached ? { ...defaults, ...cached } : defaults;

  apiGet('cms-api/footer/')
    .then(fresh => {
      writeCache('footer', fresh);
      if (JSON.stringify(fresh) !== JSON.stringify(cached) && onUpdate) {
        onUpdate({ ...defaults, ...fresh });
      }
    })
    .catch(() => {});

  return immediate;
}

/** Fetch an InsightPage preview by token */
export async function insightPreview(token) {
  try { return await apiGet(`insights/preview/?token=${encodeURIComponent(token)}`); }
  catch { return null; }
}

export const isPreview = () => !!previewToken();
